import * as React from 'react'
import { cn } from '../../lib/utils'
import { type VariantProps } from 'class-variance-authority'
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '../form'
import type { inputContainerVariants } from './input-container'
import type { TypographySize } from '../typography'

const labelContainerTextSizeVariants: Record<
  'sm' | 'md' | 'lg',
  TypographySize
> = {
  sm: '2xs',
  md: 'xs',
  lg: 'sm',
}

const helperContainerTextSizeVariants: Record<
  'sm' | 'md' | 'lg',
  TypographySize
> = {
  sm: '2xs',
  md: 'xs',
  lg: 'sm',
}

export interface FormItemWrapperProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof inputContainerVariants> {
  title?: React.ReactNode
  caption?: React.ReactNode | string
}

const FormItemWrapper = React.forwardRef<HTMLDivElement, FormItemWrapperProps>(
  ({ title, caption, inputSize, className, children }, ref) => {
    const sizeKey = inputSize ?? 'md'

    return (
      <FormItem
        className={cn('w-full flex flex-col space-y-1.5', className)}
        ref={ref}
      >
        {title && (
          <FormLabel
            size={labelContainerTextSizeVariants[sizeKey]}
            color='text-neutral-surface-100'
            weight='bold'
          >
            {title}
          </FormLabel>
        )}
        <FormControl>{children}</FormControl>

        {caption && (
          <FormDescription
            size={helperContainerTextSizeVariants[sizeKey]}
            color='text-neutral-surface-100'
            weight='regular'
          >
            {caption}
          </FormDescription>
        )}
        <FormMessage
          size={helperContainerTextSizeVariants[sizeKey]}
          color='text-negative-surface-100'
          weight='regular'
          haveCaption={!!caption}
        />
      </FormItem>
    )
  },
)

FormItemWrapper.displayName = 'FormItemWrapper'

export { FormItemWrapper }
